import { useMemo } from 'react'
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { Observable, relayStylePagination } from '@apollo/client/utilities'
import { RestLink } from 'apollo-link-rest'

// ##### BELOW IS THE RELAY PAGINATION CODE PULLED FROM APOLLO/CLIENT
// ##### THIS WILL HELP US UNDERSTAND HOW TO CUSTOMIZE SOMETHING IF WE NEED IT
// ##### AS RIGHT NOW IT IS JUST BLACK MAGIC.

// Whether TEdge<TNode> is a normalized Reference or a non-normalized
// object, it needs a .cursor property where the relayStylePagination
// merge function can store cursor strings taken from pageInfo. Storing an
// extra reference.cursor property should be safe, and is easier than
// attempting to update the cursor field of the normalized StoreObject
// that the reference refers to, or managing edge wrapper objects
// (something I attempted in #7023, but abandoned because of #7088).

import { BASE_URL, getRuntimeEnvironment } from '@/constants/config'
import { areDomainGraphQlErrors, graphQlErrorLogDetails } from '@/utils/graphqlError'
import { AuthContext, setSignOutCallback } from '@/stores/auth'

// import { relayStylePagination } from '@apollo/client/utilities'

function createCache() {
  return new InMemoryCache({
    typePolicies: {
      PaymentMethod: {
        keyFields: ['id'],
        fields: {
          kind: {
            merge: true
          },
          info: {
            merge: true
          },
          version: {
            merge: true
          }
        }
      },
      User: {
        keyFields: ['id', 'flags'],
        fields: {
          flags: {
            merge: true
          },
          displayName: {
            merge: true
          },
          paymentDetails: {
            merge: true
          },
          rating: {
            merge: true
          },
          files: {
            merge: true
          }
        }
      },
      Order: {
        keyFields: ['id', 'status', 'price', 'paymentRange'],
        fields: {
          price: {
            merge: true
          },
          paymentRange: {
            merge: true
          }
        }
      },
      OrderAd: {
        keyFields: ['id', 'status', 'price', 'paymentRange', 'version'],
        fields: {
          price: {
            merge: true
          },
          paymentRange: {
            merge: true
          }
        }
      },
      Announcement: {
        keyFields: ['announcementNumber', 'status', 'area'],
        fields: {
          status: {
            merge: true
          },
          area: {
            merge: true
          }
        }
      },
      Advertisement: {
        keyFields: ['advertisementNumber', 'status', 'area'],
        fields: {
          status: {
            merge: true
          },
          area: {
            merge: true
          }
        }
      },
      PaymentMethodsPresignedPuts: {
        keyFields: ['bankCard', 'wechatPay', 'linePay', 'applePay', 'jkoPay']
      },
      Query: {
        fields: {
          getWalletByCurrency: relayStylePagination(['currencyKind']),
          userWalletEventsConnection: relayStylePagination([
            'systemId',
            'orderId',
            'partnerName',
            'party',
            'partyPartnerName',
            'partyPhone',
            'phone',
            'address',
            'correlationId',
            'eventKind',
            'currencyKind',
            'isPayIn',
            'timeRange',
            'amountRange',
            'status',
            'suggestedAmountRange'
          ]),
          usersConnection: relayStylePagination(),
          ordersConnection: relayStylePagination([
            'status',
            'userId',
            'amountCurrencyKind',
            'reverse',
            'priceCurrencyKind',
            'price'
          ]),
          orderAdsConnection: relayStylePagination([
            'status',
            'userId',
            'amountCurrencyKind',
            'reverse',
            'priceCurrencyKind',
            'price'
          ]),
          assetsDepositsConnection: relayStylePagination(['currencyKind']),
          assetsWithdrawalsConnection: relayStylePagination(['currencyKind']),
          getSellOrderConnection: relayStylePagination(),
          getBuyOrderConnection: relayStylePagination(['status']),
          notificationConnection: relayStylePagination()
        }
      }
    }
  })
}

function fetchWithTimeout(uri: URL | RequestInfo, options = {}): Promise<Response> {
  return new Promise((resolve, reject) => {
    fetch(uri, { ...options, signal: AbortSignal.timeout(30 * 1000) }).then(
      (response) => {
        resolve(response)
      },
      (err) => {
        reject(err)
      }
    )
  })
}

// remove  __typename
const cleanTypename = new ApolloLink((operation, forward) => {
  const omitTypename = (key: string, value: string) => (key === '__typename' ? undefined : value)
  if (operation.variables && !operation.getContext().hasUpload) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
  }
  return forward(operation)
})

function GraphqlProvider({ children }: { children: React.ReactNode }) {
  const auth = AuthContext.useActorRef()

  const client = useMemo(() => {
    const cache = createCache()
    const restLink = new RestLink({ uri: BASE_URL })
    const graphQlEndpoint = `${BASE_URL}/mobile/secure/graphql`
    const apiHost = new URL(BASE_URL).host
    console.info('GRAPHQL_ENDPOINT_CONFIGURED', { apiHost, runtimeEnvironment: getRuntimeEnvironment() })
    const httpLink = new HttpLink({ uri: graphQlEndpoint, fetch: fetchWithTimeout })
    const errLink = onError(({ forward, operation, graphQLErrors, networkError }) => {
      const unauthorizedGraphQLError = graphQLErrors?.some((err) => Number(err.extensions?.code) === 4000)
      const unauthorizedNetworkError = Boolean(
        networkError && 'statusCode' in networkError && networkError.statusCode === 401
      )

      if ((unauthorizedNetworkError || unauthorizedGraphQLError) && auth.getSnapshot().matches('LOGGED_IN')) {
        return new Observable((observer) => {
          const refreshAlreadyInFlight = auth.getSnapshot().matches({ LOGGED_IN: 'REFRESHING' })
          let retrySubscription: { unsubscribe: () => void } | undefined

          if (!refreshAlreadyInFlight) {
            console.info('AUTH_REFRESH_STARTED', { operation: operation.operationName || 'anonymous' })
          }
          auth.send({ type: 'REFRESH' })

          const authSubscription = auth.subscribe((state) => {
            if (!state.matches({ LOGGED_IN: 'REFRESHING' })) {
              if (state.matches('LOGGED_IN')) {
                authSubscription.unsubscribe()
                retrySubscription = forward(operation).subscribe({
                  next: (value) => observer.next(value),
                  error: (error) => observer.error(error),
                  complete: () => observer.complete()
                })
              } else {
                authSubscription.unsubscribe()
                observer.complete()
              }
            }
          })

          return () => {
            authSubscription.unsubscribe()
            retrySubscription?.unsubscribe()
          }
        })
      }

      if (graphQLErrors?.length) {
        const details = graphQlErrorLogDetails(operation.operationName, graphQLErrors)
        if (areDomainGraphQlErrors(graphQLErrors)) {
          console.info('GRAPHQL_DOMAIN_RESULT', details)
        } else {
          console.warn('GRAPHQL_ERROR', details)
        }
      }
      if (networkError && !unauthorizedNetworkError) {
        const statusCode = 'statusCode' in networkError ? networkError.statusCode : undefined
        console.warn('GRAPHQL_NETWORK_ERROR', {
          operation: operation.operationName || 'anonymous',
          statusCode: typeof statusCode === 'number' ? statusCode : undefined,
          name: networkError.name,
          apiHost
        })
      }
    })

    const jwtLink = setContext((_, { headers }) => {
      const jwt = auth.getSnapshot().context.values?.jwt
      return jwt ? { headers: { ...headers, authorization: `Bearer ${jwt}` } } : { headers }
    })

    const client = new ApolloClient({
      cache,
      link: ApolloLink.from([cleanTypename, errLink, jwtLink, restLink, httpLink]),
      defaultOptions: {
        watchQuery: {
          notifyOnNetworkStatusChange: true,
          fetchPolicy: 'cache-and-network'
        },
        query: {
          notifyOnNetworkStatusChange: true,
          fetchPolicy: 'network-only'
        }
      }
    })

    setSignOutCallback(() => {
      client.cache.reset()
    })

    return client
  }, [auth])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default GraphqlProvider
